import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Widget heights: half-size widgets share a fixed HALF_HEIGHT (they sit side
// by side, so uniformity matters). Full-size widgets stack vertically, so
// `tall` ones hug their content up to TALL_MAX (canvas fills it, a light
// schedule day shrinks to fit — no manufactured whitespace); anything longer
// scrolls inside. Full widgets without `tall` (Quick Actions) stay auto bars.
// `compact` half widgets (To Do, Client Birthdays) hug their content instead
// of stretching to HALF_HEIGHT — they cap there and scroll past it.
const HALF_HEIGHT = 430;
const TALL_MAX = 520;

// Chrome around every dashboard widget: navy title bar with a drag
// handle (only the handle starts a drag, so widget-internal buttons and
// canvas drawing are unaffected) and a hide button.
// `locked` widgets are pinned: no drag handle, no hide button, never sortable.
export default function WidgetShell({ id, title, size, tall, compact, locked, onHide, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !!locked,
  });

  const height = size === 'half' && !compact ? HALF_HEIGHT : undefined;
  const maxHeight = (size === 'full' && tall) ? TALL_MAX
    : (size === 'half' && compact) ? HALF_HEIGHT
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        gridColumn: size === 'full' ? '1 / -1' : 'auto',
        height,
        maxHeight,
        opacity: isDragging ? 0.65 : 1,
        zIndex: isDragging ? 5 : 'auto',
        background: 'var(--white)',
        borderRadius: 12,
        boxShadow: '0 1px 8px rgba(0,0,0,0.09)',
        border: '1px solid var(--border-color)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'var(--primary-color)',
          color: '#fff',
          padding: '8px 12px',
          flexShrink: 0,
        }}
      >
        {!locked && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            title="Drag to move"
            aria-label={`Move ${title} widget`}
            style={{
              cursor: 'grab',
              background: 'transparent',
              border: 'none',
              color: '#fff',
              fontSize: 15,
              padding: '2px 4px',
              touchAction: 'none',
            }}
          >
            ✥
          </button>
        )}
        <span style={{ fontWeight: 700, fontSize: 14, flex: 1, paddingLeft: locked ? 4 : 0 }}>{title}</span>
        {!locked && (
          <button
            type="button"
            onClick={onHide}
            title="Hide widget"
            aria-label={`Hide ${title} widget`}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.85)',
              cursor: 'pointer',
              fontSize: 14,
              padding: '2px 4px',
            }}
          >
            ✕
          </button>
        )}
      </div>
      <div style={{ padding: 12, flex: 1, overflowY: 'auto' }}>{children}</div>
    </div>
  );
}
